import {Component, Input, Output, EventEmitter} from '@angular/core';
import {CommonModule} from '@angular/common';
import {GateweeIconComponent} from '../gatewee-icon/gatewee-icon.component';

type ButtonVariant = 'primary' | 'secondary' | 'outline';
type ButtonSize = 's' | 'm';

@Component({
  selector: 'gatewee-button',
  templateUrl: './gatewee-button.component.html',
  styleUrls: ['./gatewee-button.component.css'],
  standalone: true,
  imports: [CommonModule, GateweeIconComponent],
})
export class GateweeButtonComponent {
  @Input() title = '';
  @Input() variant: ButtonVariant = 'primary';
  @Input() disabled = false;
  @Input() type: 'button' | 'submit' | 'reset' = 'button';
  @Input() iconName = '';
  @Input() loading = false;
  @Input() size: ButtonSize = 'm';
  @Input() hideTitleOnSmall = false;

  @Output() clicked = new EventEmitter<Event>();

  private get sizeClasses(): string {
    const hasTitle = this.title && this.title.trim();
    const sizes: Record<ButtonSize, string> = {
      s: hasTitle
        ? 'gw-h-[32px] gw-gap-1.5 gw-px-small gw-text-xs'
        : 'gw-h-[32px] gw-w-[32px] gw-p-0 gw-text-xs',
      m: hasTitle
        ? 'gw-h-[40px] gw-gap-2 gw-px-medium'
        : 'gw-h-[40px] gw-w-[44px] gw-p-0',
    };
    return sizes[this.size] || sizes['m'];
  }

  get computedClasses(): string {
    const base =
      'gw-whitespace-nowrap gw-font-small gw-flex gw-items-center gw-justify-center gw-font-bold gw-transition-colors gw-duration-200 gw-rounded-[4px] gw-cursor-pointer ' +
      this.sizeClasses;

    const variants: Record<ButtonVariant, string> = {
      primary: this.disabled
        ? 'gw-bg-background-neutral-1 gw-text-text-tertiary gw-border gw-border-solid gw-border-background-neutral-1'
        : 'gw-bg-background-accent-1 gw-text-text-primary-on-accent-1 gw-border gw-border-solid gw-border-background-accent-1 hover:gw-bg-background-accent-1-hover active:gw-bg-background-accent-1-pressed hover:gw-border-background-accent-1-hover active:gw-border-background-accent-1-pressed',

      secondary: this.disabled
        ? 'gw-bg-background-base-alt gw-text-text-tertiary gw-border gw-border-solid gw-border-background-neutral-1'
        : 'gw-bg-background-accent-opposite gw-text-background-base gw-border gw-border-solid gw-border-background-accent-opposite hover:gw-bg-background-accent-opposite-hover active:gw-bg-background-accent-opposite-pressed',

      outline: this.disabled
        ? 'gw-bg-transparent gw-text-text-tertiary gw-border gw-border-solid gw-border-border-normal'
        : 'gw-bg-transparent gw-text-text-primary gw-border gw-border-solid gw-border-background-accent-opposite gw-border-[1px] hover:gw-bg-background-neutral-1 active:gw-bg-background-neutral-1pressed',
    };

    const sharedDisabled =
      'disabled:gw-text-text-tertiary disabled:gw-cursor-not-allowed';

    return [base, variants[this.variant], sharedDisabled].join(' ');
  }

  onClick(event: Event) {
    if (!this.disabled) {
      this.clicked.emit(event);
    }
  }
}
